import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Search, Edit2, Trash2, Eye, X, Filter, ArrowUpDown, ChevronDown, Calendar, Tag, AlertTriangle, Hash, Paperclip } from 'lucide-react';

import DeleteConfirmModal from './DeleteConfirmModal';

const formatDuration = (ms) => {
  if (!ms && ms !== 0) return null;
  if (ms < 1000) return '< 1 menit';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const parts = [];
  if (days > 0) parts.push(days + ' hari');
  if (hours % 24 > 0) parts.push((hours % 24) + ' jam');
  if (minutes % 60 > 0) parts.push((minutes % 60) + ' menit');
  if (parts.length === 0) return '< 1 menit';
  return parts.join(' ');
};

const ActivityList = ({ activities, onUpdate, onDelete, onEdit }) => {
  const getActivityId = (activity) => activity._id || activity.id;
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterBagian, setFilterBagian] = useState('All');
  const [filterTicketId, setFilterTicketId] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewingActivity, setViewingActivity] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const categories = useMemo(() => ['All', ...new Set(activities.map(a => a.category))], [activities]);
  const bagians = useMemo(() => ['All', ...new Set(activities.map(a => a.bagian || 'Umum').filter(Boolean))], [activities]);
  const statuses = ['All', 'Pending', 'In Progress', 'Completed'];
  const priorities = ['All', 'Low', 'Medium', 'High'];

  const filteredAndSortedActivities = useMemo(() => {
    let filtered = activities;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(activity =>
        activity.title.toLowerCase().includes(term) ||
        activity.description?.toLowerCase().includes(term) ||
        activity.ticketId?.toLowerCase().includes(term)
      );
    }

    if (filterTicketId) {
      filtered = filtered.filter(activity =>
        activity.ticketId?.toLowerCase().includes(filterTicketId.toLowerCase())
      );
    }

    if (filterStartDate) {
      filtered = filtered.filter(activity => new Date(activity.date) >= new Date(filterStartDate));
    }

    if (filterEndDate) {
      filtered = filtered.filter(activity => new Date(activity.date) <= new Date(filterEndDate + 'T23:59:59'));
    }

    if (filterCategory !== 'All') {
      filtered = filtered.filter(activity => activity.category === filterCategory);
    }

    if (filterStatus !== 'All') {
      filtered = filtered.filter(activity => activity.status === filterStatus);
    }

    if (filterPriority !== 'All') {
      filtered = filtered.filter(activity => activity.priority === filterPriority);
    }

    if (filterBagian !== 'All') {
      filtered = filtered.filter(activity => (activity.bagian || 'Umum') === filterBagian);
    }

    filtered.sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'priority':
          const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          aValue = priorityOrder[a.priority];
          bValue = priorityOrder[b.priority];
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
      }
      if (sortOrder === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [activities, searchTerm, filterCategory, filterStatus, filterPriority, filterBagian, filterTicketId, filterStartDate, filterEndDate, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-300" />;
    return (
      <span className="ml-1 inline-flex flex-col leading-none">
        <ChevronDown className={`w-3 h-3 -mb-1 ${sortOrder === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
        <ChevronDown className={`w-3 h-3 rotate-180 ${sortOrder === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
      </span>
    );
  };

  const getInitials = (title) => title.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

  const SortHeader = ({ field, children }) => (
    <th onClick={() => handleSort(field)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none">
      <div className="flex items-center">{children}{getSortIcon(field)}</div>
    </th>
  );

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Activities</h2>
          <p className="mt-1 text-sm text-gray-500">Manage and track all your activities</p>
        </div>
        <div className="flex items-center space-x-2">
          <button onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary text-xs ${showFilters || searchTerm || filterCategory !== 'All' || filterStatus !== 'All' || filterPriority !== 'All' || filterBagian !== 'All' || filterTicketId ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}`}>
            <Filter className="w-3.5 h-3.5" /><span>Filters</span>
          </button>
          <div className="bg-blue-50 rounded-lg px-3 py-2">
            <span className="text-sm font-medium text-blue-700">{filteredAndSortedActivities.length} of {activities.length}</span>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="card">
        <div className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="text" placeholder="Search by title, description, or Ticket ID..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white" />
              {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
            </div>
          </div>

          {showFilters && <>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3 pt-3 border-t border-gray-100 animate-slide-up">
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="select-field text-sm">
                {categories.map(category => <option key={category} value={category}>{category === 'All' ? 'All Categories' : category}</option>)}
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="select-field text-sm">
                {statuses.map(status => <option key={status} value={status}>{status === 'All' ? 'All Statuses' : status}</option>)}
              </select>
              <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="select-field text-sm">
                {priorities.map(priority => <option key={priority} value={priority}>{priority === 'All' ? 'All Priorities' : priority}</option>)}
              </select>
              <select value={filterBagian} onChange={(e) => setFilterBagian(e.target.value)} className="select-field text-sm">
                {bagians.map(bagian => <option key={bagian} value={bagian}>{bagian === 'All' ? 'All Bagian' : bagian}</option>)}
              </select>
              <input type="text" placeholder="Filter by Ticket ID..." value={filterTicketId}
                onChange={(e) => setFilterTicketId(e.target.value)} className="input-field text-sm" />
              <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} className="input-field text-sm" title="Start Date" />
              <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} className="input-field text-sm" title="End Date" />
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <select value={`${sortBy}-${sortOrder}`} onChange={(e) => { const [field, order] = e.target.value.split('-'); setSortBy(field); setSortOrder(order); }} className="select-field text-sm" title="Sort by">
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="title-asc">Title A-Z</option>
                <option value="title-desc">Title Z-A</option>
                <option value="priority-desc">Priority High-Low</option>
                <option value="priority-asc">Priority Low-High</option>
              </select>
              {(filterStartDate || filterEndDate) && (
                <button onClick={() => { setFilterStartDate(''); setFilterEndDate(''); }} className="text-xs text-blue-600 font-medium hover:text-blue-700">
                  Clear dates
                </button>
              )}
            </div>
          </>}
        </div>
      </div>

      {/* Activity Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader field="title">Title</SortHeader>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-[120px]">Actions</th>
                <SortHeader field="status">Status</SortHeader>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ticket ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bagian</th>
                <SortHeader field="priority">Priority</SortHeader>
                <SortHeader field="date">Date</SortHeader>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Created By</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {filteredAndSortedActivities.length > 0 ? (
                filteredAndSortedActivities.map((activity, idx) => (
                  <tr key={getActivityId(activity)} className="hover:bg-gray-50 transition-colors" style={{ animationDelay: `${idx * 20}ms` }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold text-white ${activity.status === 'Completed' ? 'bg-green-500' : activity.status === 'In Progress' ? 'bg-yellow-500' : 'bg-gray-400'}`}>
                          {getInitials(activity.title)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">{activity.title}</div>
                          {activity.description && <div className="text-xs text-gray-400 truncate max-w-[200px]">{activity.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <button onClick={() => setViewingActivity(activity)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="View details"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => onEdit(activity)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Edit"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteConfirm(activity)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(activity.status)}`}>
                        {activity.status === 'In Progress' ? 'In Progress' : activity.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {activity.ticketId ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono text-gray-500 bg-gray-100 rounded">
                          <Hash className="w-3 h-3 mr-1" />{activity.ticketId}
                        </span>
                      ) : <span className="text-xs text-gray-300 italic">-</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        <Tag className="w-3 h-3 mr-1" />{activity.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{activity.bagian || 'Umum'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${getPriorityColor(activity.priority)}`}>{activity.priority}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center space-x-1"><Calendar className="w-3.5 h-3.5 text-gray-400" /><span>{format(new Date(activity.date), 'MMM dd, yyyy HH:mm')}</span></div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {activity.createdBy?.name || activity.createdBy?.username || '-'}
                      {activity.createdBy?.bagian && <span className="text-gray-400 ml-1">({activity.createdBy.bagian})</span>}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4"><AlertTriangle className="w-7 h-7 text-gray-400" /></div>
                      <p className="text-gray-500 font-medium">No activities found</p>
                      <p className="text-gray-400 text-sm mt-1">{searchTerm || filterCategory !== 'All' || filterStatus !== 'All' || filterPriority !== 'All' || filterBagian !== 'All' ? 'Try adjusting your search or filters' : 'Add an activity to get started'}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={async () => {
          if (isDeleting || !deleteConfirm) return;
          setIsDeleting(true);
          try {
            await onDelete(getActivityId(deleteConfirm));
            setDeleteConfirm(null);
          } catch (err) {
            setDeleteConfirm(null);
          } finally {
            setIsDeleting(false);
          }
        }}
        title="Hapus Activity"
        isLoading={isDeleting}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Judul</span>
          <span className="text-sm font-semibold text-gray-900 text-right max-w-[250px]">{deleteConfirm?.title}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Status</span>
          <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${
            deleteConfirm?.status === 'Completed' ? 'bg-green-100 text-green-800 border-green-200' :
            deleteConfirm?.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
            'bg-gray-100 text-gray-800 border-gray-200'
          }`}>{deleteConfirm?.status}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Kategori</span>
          <span className="text-xs font-semibold text-gray-900">{deleteConfirm?.category}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Prioritas</span>
          <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${
            deleteConfirm?.priority === 'High' ? 'bg-red-100 text-red-800 border-red-200' :
            deleteConfirm?.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
            'bg-blue-100 text-blue-800 border-blue-200'
          }`}>{deleteConfirm?.priority}</span>
        </div>
        {deleteConfirm?.createdBy?.name && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-medium">Dibuat oleh</span>
            <span className="text-xs font-semibold text-gray-900">{deleteConfirm.createdBy.name}</span>
          </div>
        )}
      </DeleteConfirmModal>

      {/* View Details Modal */}
      {viewingActivity && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => setViewingActivity(null)}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-base font-bold text-white ${viewingActivity.status === 'Completed' ? 'bg-green-500' : viewingActivity.status === 'In Progress' ? 'bg-yellow-500' : 'bg-gray-400'}`}>
                    {getInitials(viewingActivity.title)}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{viewingActivity.title}</h3>
                    {viewingActivity.description && <p className="text-sm text-gray-500 mt-0.5">{viewingActivity.description}</p>}
                  </div>
                </div>
                <button onClick={() => setViewingActivity(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="px-6 py-5 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Ticket ID</p>
                    <p className="text-sm font-mono font-semibold text-gray-900">{viewingActivity.ticketId || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Category</p>
                    <p className="text-sm font-semibold text-gray-900">{viewingActivity.category}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Bagian</p>
                    <p className="text-sm font-semibold text-gray-900">{viewingActivity.bagian || 'Umum'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Created By</p>
                    <p className="text-sm font-semibold text-gray-900">{viewingActivity.createdBy?.name || viewingActivity.createdBy?.username || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Status</p>
                    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${getStatusColor(viewingActivity.status)}`}>{viewingActivity.status}</span>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Priority</p>
                    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(viewingActivity.priority)}`}>{viewingActivity.priority}</span>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Start Date</p>
                    <p className="text-sm font-semibold text-gray-900">{format(new Date(viewingActivity.date), 'MMM dd, yyyy HH:mm')}</p>
                  </div>
                  {viewingActivity.endDate && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">End Date</p>
                      <p className="text-sm font-semibold text-gray-900">{format(new Date(viewingActivity.endDate), 'MMM dd, yyyy HH:mm')}</p>
                    </div>
                  )}
                  {viewingActivity.status === 'Completed' && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">⏱ Durasi</p>
                      <p className="text-sm font-bold text-blue-700">{formatDuration(viewingActivity.actualDuration || (viewingActivity.completedAt ? new Date(viewingActivity.completedAt) - new Date(viewingActivity.createdAt) : 0))}</p>
                    </div>
                  )}
                  {viewingActivity.lampiran && (
                    <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Lampiran</p>
                      <a
                        href={`http://localhost:5000${viewingActivity.lampiran}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                        <span>View Attachment</span>
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-2">
                <button onClick={() => setViewingActivity(null)} className="btn-secondary text-sm">Close</button>
                <button onClick={() => { const act = viewingActivity; setViewingActivity(null); onEdit(act); }} className="btn-primary text-sm"><Edit2 className="w-4 h-4" /><span>Edit</span></button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityList;
